import { useMemo, useState } from "react";
import { Users, Plus, Pencil, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import AppSidebar from "@/components/AppSidebar";
import { PageTransition } from "@/components/PageTransition";
import { TeamToolbar } from "@/components/team/TeamToolbar";
import { TeamMemberDialog } from "@/components/team/TeamMemberDialog";
import { BulkAddDialog } from "@/components/team/BulkAddDialog";
import { GearBadge } from "@/components/team/GearBadge";
import { useSidebar } from "@/contexts/SidebarContext";
import { useTeamMembers } from "@/hooks/useTeamMembers";
import {
  defaultTeamFilter,
  distinctGearTypes,
  emptyForm,
  filterAndSortMembers,
  type FormState,
  type TeamFilter,
  type TeamMember,
} from "@/lib/teamMembers";

const MyTeam = () => {
  const { expanded } = useSidebar();
  const { membersQuery, saveMutation, bulkSaveMutation, deleteMutation } = useTeamMembers();
  const { data: members = [], isLoading } = membersQuery;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [initialForm, setInitialForm] = useState<FormState>(emptyForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filter, setFilter] = useState<TeamFilter>(defaultTeamFilter);

  const visibleMembers = useMemo(() => filterAndSortMembers(members, filter), [members, filter]);

  const gearOptions = useMemo(() => distinctGearTypes(members), [members]);

  const openEdit = (m: TeamMember) => {
    setEditingId(m.id);
    setInitialForm({
      name: m.name,
      email: m.email,
      phone: m.phone || "",
      role: m.role || "",
      gear: m.gear,
    });
    setDialogOpen(true);
  };

  const openAdd = () => {
    setEditingId(null);
    setInitialForm(emptyForm);
    setDialogOpen(true);
  };

  const handleSubmit = (form: FormState) => {
    saveMutation.mutate(
      { form, editingId },
      {
        onSuccess: () => {
          setDialogOpen(false);
          setEditingId(null);
          setInitialForm(emptyForm);
        },
      },
    );
  };

  return (
    <PageTransition>
      <div className="flex min-h-screen bg-background">
        <AppSidebar />
        <main
          className="flex-1 transition-all duration-300"
          style={{ marginLeft: expanded ? 220 : 72 }}
        >
          <div className="w-full px-6 py-10 lg:px-10">
            {/* Header */}
            <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Users className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-bold">My Team</h1>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setBulkOpen(true)}>
                  <Upload className="mr-2 h-4 w-4" />
                  Add multiple
                </Button>
                <Button onClick={openAdd}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add member
                </Button>
              </div>
            </div>

            {/* Toolbar */}
            {!isLoading && members.length > 0 && (
              <TeamToolbar
                filter={filter}
                onChange={setFilter}
                gearOptions={gearOptions}
                resultCount={visibleMembers.length}
                totalCount={members.length}
              />
            )}

            {/* Members list */}
            {isLoading ? (
              <Card className="h-40 animate-pulse p-5" />
            ) : members.length === 0 ? (
              <Card className="p-12 text-center text-muted-foreground">
                <Users className="mx-auto mb-3 h-10 w-10 opacity-40" />
                <p>No team members yet.</p>
                <p className="mt-1 text-sm">Add your frequent crew collaborators here.</p>
              </Card>
            ) : visibleMembers.length === 0 ? (
              <Card className="p-12 text-center text-muted-foreground">
                <p>No members match your search or filters.</p>
              </Card>
            ) : (
              <Card className="overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Gear</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleMembers.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell className="font-medium">{m.name}</TableCell>
                        <TableCell className="capitalize text-muted-foreground">
                          {m.role || "—"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{m.email}</TableCell>
                        <TableCell className="text-muted-foreground">{m.phone || "—"}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {m.gear
                              .filter((g) => g.type)
                              .map((g) => (
                                <GearBadge key={g.type} gear={g} />
                              ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(m)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => setDeleteId(m.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        </main>

        {/* Add / Edit single member */}
        <TeamMemberDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          editingId={editingId}
          initialForm={initialForm}
          saving={saveMutation.isPending}
          onSubmit={handleSubmit}
        />

        {/* Bulk add */}
        <BulkAddDialog
          open={bulkOpen}
          onOpenChange={setBulkOpen}
          existingMembers={members}
          saving={bulkSaveMutation.isPending}
          onSubmit={(rows) =>
            bulkSaveMutation.mutate(rows, { onSuccess: () => setBulkOpen(false) })
          }
        />

        {/* Delete confirmation */}
        <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Remove team member?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              This removes them from your saved team. It won't affect existing productions.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteId(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={deleteMutation.isPending}
                onClick={() =>
                  deleteId &&
                  deleteMutation.mutate(deleteId, { onSuccess: () => setDeleteId(null) })
                }
              >
                {deleteMutation.isPending ? "Removing..." : "Remove"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PageTransition>
  );
};

export default MyTeam;
